import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	AsyncDelayService,
	LOGOUT_EVENT,
	LogoutService,
} from '@services/public';
import { HTTP_CACHE_MAX_AGE, SelectKey } from '@util';
import { ChartConfiguration } from 'chart.js';
import { config } from 'environment';
import { Observer, ReplaySubject, Subscription } from 'rxjs';
import {
	ImageChartEntry,
	KeySubscription,
	LeaderReportAPIResponse,
	LeaderReportFilters,
	LeaderReportFilterService,
	LeaderReportInitializationService,
	LeaderReportType,
	OverallScoresColumn,
} from './report';

// Cache expiration in milliseconds.
const CACHE_EXPIRATION = 300000;

export class Report<T> {
	active: boolean = false;
	viewAllSubscription: Subscription;

	private _subject: ReplaySubject<T> = new ReplaySubject<T>(1);

	constructor(
		public filters: LeaderReportFilters,
		public generator: (data: any, ...args: any) => any,
		private _subscriptions: Array<KeySubscription>,
	) {}

	next(report: T) {
		this._subject.next(report);
	}

	complete() {
		this._subject.complete();
	}

	subscribe(
		key: symbol,
		...args: [observerOrNext?: Partial<Observer<T>> | ((value: T) => void)]
	) {
		this._subscriptions.push({
			key,
			subscription: this._subject.subscribe(...args),
		});
		this.active = true;
	}
}

export interface DashboardReports {
	reports: {
		[LeaderReportType.DOMAIN_GROWTH]: Report<ChartConfiguration>;
		[LeaderReportType.DOMAIN_OVERALL_SCORES]: Report<
			Array<OverallScoresColumn>
		>;
		[LeaderReportType.SELF_AWARENESS]: Report<Array<ImageChartEntry>>;
		[LeaderReportType.SELF_BY_GROUP]: Report<ChartConfiguration>;
		[LeaderReportType.SELF_VS_PEER_BY_USER]: Report<ChartConfiguration>;
		[LeaderReportType.STRENGTHS_AND_WEAKNESSES]: Report<Array<ImageChartEntry>>;
	};
	filters: LeaderReportFilters;
}

export interface DetailedReports {
	reports: {
		[LeaderReportType.SELF_VS_PEER_BY_SKILL]: Report<ChartConfiguration>;
		[LeaderReportType.SKILL_GROWTH]: Report<ChartConfiguration>;
		[LeaderReportType.SKILL_OVERALL_SCORES]: Report<Array<OverallScoresColumn>>;
		[LeaderReportType.STRENGTHS_AND_WEAKNESSES]: Report<Array<ImageChartEntry>>;
	};
	filters: LeaderReportFilters;
}

interface ReportAPIOptions {
	reports: Array<LeaderReportType>;
	organizationUUID: string;
	domainUID: number;
	groupUUID?: string | null;
	userUUID?: string | null;
	skillUID?: number | null;
	timespan?: string | null;
	assessmentUUID?: string | null;
}

@Injectable({
	providedIn: 'root',
})
export class LeaderReportService {
	// Subscriptions.
	private _subscriptions: Array<KeySubscription> = [];

	// Reports.
	private _reports: { [key in LeaderReportType]: Report<any> | null } = {
		[LeaderReportType.DOMAIN_GROWTH]: null,
		[LeaderReportType.DOMAIN_OVERALL_SCORES]: null,
		[LeaderReportType.SELF_AWARENESS]: null,
		[LeaderReportType.SELF_BY_GROUP]: null,
		[LeaderReportType.SELF_VS_PEER_BY_SKILL]: null,
		[LeaderReportType.SELF_VS_PEER_BY_USER]: null,
		[LeaderReportType.SKILL_GROWTH]: null,
		[LeaderReportType.SKILL_OVERALL_SCORES]: null,
		[LeaderReportType.STRENGTHS_AND_WEAKNESSES]: null,
	};

	// Synchronous loading.
	private _loadQueue: Set<LeaderReportType> = new Set<LeaderReportType>();
	private _loadReportsKey: symbol = Symbol();

	constructor(
		private _asyncDelaySvc: AsyncDelayService,
		private _filterSvc: LeaderReportFilterService,
		private _http: HttpClient,
		private _initializationSvc: LeaderReportInitializationService,
		private _logoutSvc: LogoutService,
	) {
		// Bind logout service.
		this._logoutSvc.subscribe(LOGOUT_EVENT.POST_API, this._logout.bind(this));
	}

	async getDashboardReports(key: symbol): Promise<DashboardReports> {
		this._resetReports();

		// Generate dashboard reports object and main filters.
		const dashboardReports: DashboardReports = {
			reports: {
				[LeaderReportType.DOMAIN_GROWTH]: this._initializeReport(
					LeaderReportType.DOMAIN_GROWTH,
				),
				[LeaderReportType.DOMAIN_OVERALL_SCORES]: this._initializeReport(
					LeaderReportType.DOMAIN_OVERALL_SCORES,
				),
				[LeaderReportType.SELF_AWARENESS]: this._initializeReport(
					LeaderReportType.SELF_AWARENESS,
				),
				[LeaderReportType.SELF_BY_GROUP]: this._initializeReport(
					LeaderReportType.SELF_BY_GROUP,
				),
				[LeaderReportType.SELF_VS_PEER_BY_USER]: this._initializeReport(
					LeaderReportType.SELF_VS_PEER_BY_USER,
				),
				[LeaderReportType.STRENGTHS_AND_WEAKNESSES]: this._initializeReport(
					LeaderReportType.STRENGTHS_AND_WEAKNESSES,
				),
			},
			filters: this._filterSvc.getFilters([
				'domain',
				'organization',
				'group',
				'timespan',
				'user',
			]),
		};

		// Bind the main filters to sub filters.
		for (const filterKey in dashboardReports.filters) {
			for (const report of Object.values(dashboardReports.reports)) {
				if (!(filterKey in report.filters)) continue;
				this._subscriptions.push({
					key,
					subscription: dashboardReports.filters[filterKey].mirror(
						report.filters[filterKey],
					),
				});
			}
		}

		// Initialize the main filters.
		await this._filterSvc.initializeFilters(
			key,
			dashboardReports.filters,
			this._subscriptions,
		);

		return dashboardReports;
	}

	async getDetailedReports(key: symbol): Promise<DetailedReports> {
		this._resetReports();

		// Generate dashboard reports object and main filters.
		const detailedReports: DetailedReports = {
			reports: {
				[LeaderReportType.SELF_VS_PEER_BY_SKILL]: this._initializeReport(
					LeaderReportType.SELF_VS_PEER_BY_SKILL,
				),
				[LeaderReportType.SKILL_GROWTH]: this._initializeReport(
					LeaderReportType.SKILL_GROWTH,
				),
				[LeaderReportType.SKILL_OVERALL_SCORES]: this._initializeReport(
					LeaderReportType.SKILL_OVERALL_SCORES,
				),
				[LeaderReportType.STRENGTHS_AND_WEAKNESSES]: this._initializeReport(
					LeaderReportType.STRENGTHS_AND_WEAKNESSES,
				),
			},
			filters: this._filterSvc.getFilters([
				'domain',
				'organization',
				'assessment',
				'group',
				'skill',
				'user',
			]),
		};

		// Bind the main filters to sub filters.
		for (const filterKey in detailedReports.filters) {
			for (const report of Object.values(detailedReports.reports)) {
				if (!(filterKey in report.filters)) continue;
				this._subscriptions.push({
					key,
					subscription: detailedReports.filters[filterKey].mirror(
						report.filters[filterKey],
					),
				});
			}
		}

		// Initialize the main filters.
		await this._filterSvc.initializeFilters(
			key,
			detailedReports.filters,
			this._subscriptions,
		);

		return detailedReports;
	}

	/**
	 * Always call unsubscribe in the component's onDestroy event function.
	 * @param {symbol} key The unique key for the component being destroyed.
	 */
	unsubscribe(key: symbol): void {
		for (let i: number = 0; i < this._subscriptions.length; i++) {
			if (this._subscriptions[i].key === key) {
				this._subscriptions.splice(i--, 1)[0].subscription.unsubscribe();
			}
		}
		if (this._subscriptions.length === 0) this._deleteReports();
	}

	private _initializeReport<T>(type: LeaderReportType): Report<T> {
		const report = new Report<T>(
			this._filterSvc.getFilters(this._initializationSvc.filters(type)),
			this._initializationSvc.generator(type),
			this._subscriptions,
		);
		for (const filter of Object.values(report.filters)) {
			if (filter.key === SelectKey.VIEW_ALL) continue;
			filter.onChange.subscribe(() => {
				if (!report.active) return;
				this._loadQueue.add(type);
				this._asyncDelaySvc.asyncDelay(
					this._loadReportsKey,
					this._loadReports.bind(this),
				);
			});
		}
		return (this._reports[type] = report);
	}

	private _loadReports(): void {
		// Group reports by filter values.
		const batches: Array<ReportAPIOptions> = [];
		for (const reportType of this._loadQueue) {
			const reportFilters = this._reports[reportType].filters;
			const batch = batches.find(
				(batch) =>
					batch.assessmentUUID ===
						(reportFilters.assessment?.value?.uuid ?? null) &&
					batch.domainUID === reportFilters.domain.value.uid &&
					batch.groupUUID === (reportFilters.group?.value?.uuid ?? null) &&
					batch.organizationUUID === reportFilters.organization.value.uuid &&
					batch.skillUID === (reportFilters.skill?.value?.uid ?? null) &&
					batch.timespan === (reportFilters.timespan?.value?.value ?? null) &&
					batch.userUUID === (reportFilters.user?.value?.uuid ?? null),
			);
			if (!batch) {
				batches.push({
					reports: [reportType],
					assessmentUUID: reportFilters.assessment?.value?.uuid ?? null,
					domainUID: reportFilters.domain.value.uid,
					groupUUID: reportFilters.group?.value?.uuid ?? null,
					organizationUUID: reportFilters.organization.value.uuid,
					skillUID: reportFilters.skill?.value?.uid ?? null,
					timespan: reportFilters.timespan?.value?.value ?? null,
					userUUID: reportFilters.user?.value?.uuid ?? null,
				});
			} else batch.reports.push(reportType);
		}

		// Reset the load queue.
		this._loadQueue = new Set<LeaderReportType>();

		// Load each batch.
		for (const batch of batches) this._loadReportBatch(batch);
	}

	private _loadReportBatch(options: ReportAPIOptions): void {
		// Ignore invalid batches.
		if (
			!options.reports ||
			!Array.isArray(options.reports) ||
			options.reports.length === 0 ||
			!options.domainUID ||
			!options.organizationUUID
		)
			return;

		// Build query parameters.
		const queries = [];
		Object.entries(options).forEach(([key, value]) => {
			if (!value) return;
			if (key === 'reports') {
				queries.push(`${key}=${encodeURIComponent(value.join(','))}`);
			} else queries.push(`${key}=${encodeURIComponent(value)}`);
		});

		// Send API request.
		this._http
			.get<LeaderReportAPIResponse>(
				`${config.apiBase}leader/assessment/report/view?${queries.join('&')}`,
				{
					context: new HttpContext().set(HTTP_CACHE_MAX_AGE, CACHE_EXPIRATION),
				},
			)
			.subscribe((res: LeaderReportAPIResponse) => {
				Object.entries(res).forEach(async ([key, value]) => {
					const report: Report<any> = this._reports[key];
					switch (key) {
						case LeaderReportType.SELF_AWARENESS:
						case LeaderReportType.SELF_VS_PEER_BY_SKILL:
						case LeaderReportType.STRENGTHS_AND_WEAKNESSES:
							report.next(
								await report.generator(value, report.filters.domain.value),
							);
							break;
						case LeaderReportType.SELF_BY_GROUP:
						case LeaderReportType.SELF_VS_PEER_BY_USER:
							if (!!report.viewAllSubscription) {
								report.viewAllSubscription.unsubscribe();
							}
							report.viewAllSubscription =
								report.filters.viewAll.onChange.subscribe(async () => {
									const chartConfiguration = await report.generator(
										value,
										report.filters.viewAll,
									);
									if (chartConfiguration !== null) {
										report.next(chartConfiguration);
									}
								});
							const chartConfiguration = await report.generator(
								value,
								report.filters.viewAll,
							);
							if (chartConfiguration !== null) {
								report.next(chartConfiguration);
							}
							break;
						default:
							report.next(await report.generator(value));
					}
				});
			});
	}

	private _deleteReports(): void {
		for (const reportType in this._reports) {
			if (!!this._reports[reportType]) {
				this._reports[reportType].complete();
				this._reports[reportType] = null;
			}
		}
	}

	private _resetReports(): void {
		for (const keySubscription of this._subscriptions) {
			keySubscription.subscription.unsubscribe();
		}
		this._subscriptions.splice(0);
		this._deleteReports();
	}

	private _logout(): void {
		this._resetReports();
	}
}
